<script>
import { marked } from 'marked';
import changelogRaw from '@/CHANGELOG.user.md';

// Extrait la section markdown de la version donnée (du titre "## [x.y.z]"
// jusqu'au prochain titre "## [" ou la fin du fichier).
function extractVersionSection(raw, version) {
  const lines = raw.split('\n');
  const start = lines.findIndex((line) => line.trimStart().startsWith(`## [${version}]`));
  if (start === -1) return null;
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    if (lines[i].trimStart().startsWith('## [')) {
      end = i;
      break;
    }
  }
  return lines.slice(start, end).join('\n');
}

export default {
  emits: ['close'],
  data() {
    const version = process.env.VUE_APP_VERSION;
    const section = extractVersionSection(changelogRaw, version);
    return {
      version,
      // Repli sur le changelog complet si la version courante n'est pas trouvée.
      parsedContent: marked(section ?? changelogRaw),
    };
  },
};
</script>

<template>
  <div class="changelog-backdrop" @click.self="$emit('close')">
    <div class="changelog-panel">
      <div class="changelog-header">
        <div class="changelog-title-row">
          <h2 class="changelog-title">{{ $t('changelog.title') }}</h2>
          <span class="version-badge">v{{ version }}</span>
        </div>
        <button class="close-btn" @click="$emit('close')">&times;</button>
      </div>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div class="changelog-body" v-html="parsedContent" />
    </div>
  </div>
</template>

<style scoped>
.changelog-backdrop {
  position: fixed;
  z-index: 100;
  inset: 0;
  background-color: var(--half-transparent);
  display: flex;
  justify-content: center;
  align-items: center;
}

.changelog-panel {
  background: var(--bg-secondary);
  border-radius: 10px;
  width: 680px;
  max-width: 92vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.changelog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border-secondary);
  flex-shrink: 0;
}

.changelog-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.changelog-title {
  margin: 0;
  font-size: 1.2rem;
  color: var(--text);
}

.version-badge {
  background: var(--button-background);
  color: var(--text);
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  color: var(--text-tertiary);
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

@media (hover: hover) {
  .close-btn:hover {
    background-color: var(--bg-tertiary);
    color: var(--text);
  }
}

.changelog-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

:deep(.changelog-body h2) {
  display: none;
}

:deep(.changelog-body h3) {
  font-size: 1rem;
  color: var(--selected-text);
  margin: 20px 0 8px;
  font-weight: 600;
}

:deep(.changelog-body h3:first-child) {
  margin-top: 0;
}

:deep(.changelog-body ul) {
  margin: 0 0 12px;
  padding-left: 20px;
}

:deep(.changelog-body li) {
  color: var(--text-tertiary);
  margin-bottom: 6px;
  line-height: 1.5;
  font-size: 0.9rem;
}

:deep(.changelog-body li strong) {
  color: var(--text);
  font-weight: 600;
}

:deep(.changelog-body p) {
  color: var(--text-tertiary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 8px;
}

@media screen and (max-width: 769px) {
  .changelog-panel {
    position: fixed;
    inset: 0;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
    width: 100%;
  }
}
</style>
